/**
 * Gamepad control via the Gamepad API.
 * Button layout from `Joystick.c` / README.txt (Xbox 360):
 *   Y = up · A = down · X = left · B = right · RB = bonus
 * plus the D-pad and the left stick. Gamepad index i drives player i.
 *
 * Same `poll()` shape as `KeyboardController`, so `App` can merge the two.
 */

import type { PlayerInput } from "../sim/match";
import type { TurnInput } from "../sim/types";
import { Direction } from "../sim/types";

const STANDARD = {
  A: 0,
  B: 1,
  X: 2,
  Y: 3,
  RB: 5,
  dpadUp: 12,
  dpadDown: 13,
  dpadLeft: 14,
  dpadRight: 15,
} as const;

const STICK_DEADZONE = 0.5;

export class GamepadController {
  /** Previous frame's "is this direction/bonus held" per gamepad, for edge detection. */
  private readonly prev = new Map<number, Set<string>>();

  /** Reads connected gamepads and returns one `PlayerInput` per gamepad slot. */
  poll(maxPlayers: number): PlayerInput[] {
    const out: PlayerInput[] = Array.from({ length: maxPlayers }, () => ({
      turn: null,
      useBonus: false,
    }));
    const pads = navigator.getGamepads?.() ?? [];

    for (let i = 0; i < pads.length && i < maxPlayers; i++) {
      const pad = pads[i];
      if (!pad) continue;

      const held = this.readHeld(pad);
      const before = this.prev.get(i) ?? new Set<string>();

      const turn = firstNew(before, held, [
        ["up", Direction.Up],
        ["down", Direction.Down],
        ["left", Direction.Left],
        ["right", Direction.Right],
      ]);

      out[i] = {
        turn,
        useBonus: held.has("bonus") && !before.has("bonus"),
      };
      this.prev.set(i, held);
    }

    return out;
  }

  private readHeld(pad: Gamepad): Set<string> {
    const held = new Set<string>();
    const btn = (n: number): boolean => pad.buttons[n]?.pressed ?? false;
    const [ax = 0, ay = 0] = pad.axes;

    if (btn(STANDARD.Y) || btn(STANDARD.dpadUp) || ay < -STICK_DEADZONE) held.add("up");
    if (btn(STANDARD.A) || btn(STANDARD.dpadDown) || ay > STICK_DEADZONE) held.add("down");
    if (btn(STANDARD.X) || btn(STANDARD.dpadLeft) || ax < -STICK_DEADZONE) held.add("left");
    if (btn(STANDARD.B) || btn(STANDARD.dpadRight) || ax > STICK_DEADZONE) {
      held.add("right");
    }
    if (btn(STANDARD.RB)) held.add("bonus");
    return held;
  }
}

function firstNew(
  before: Set<string>,
  now: Set<string>,
  table: ReadonlyArray<readonly [string, TurnInput]>,
): TurnInput | null {
  for (const [name, dir] of table) {
    if (now.has(name) && !before.has(name)) return dir;
  }
  return null;
}
